import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Button,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import { ArrowBack, Edit, PictureAsPdf, Upload, Delete, Description, DirectionsBus, GetApp, PictureAsPdf as DocsPrintIcon, Print as PrintIcon, ExitToApp, PhotoCamera, Visibility, CheckCircle, Schedule, Cancel, Send, ContentCopy } from '@mui/icons-material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { studentAPI, userManagementAPI, formsAPI, getParentPacketStatus, sendParentPacket, setParentPacketReviewed, remindParentPacket, resetParentPacket } from '../services/api';
import useAuthenticatedStudentPhoto from '../hooks/useAuthenticatedStudentPhoto';
import { resolveSignedCopyUi } from './signedCopyUi';
import MedicalAlertBanner from './student_profile/MedicalAlertBanner';
import MedicalSafetyTab from './student_profile/MedicalSafetyTab';
import NurseChart from './program/NurseChart';
import TherapyPrescriptionsCard from './student_profile/TherapyPrescriptionsCard';
import PaperPacketIntakePanel from './student_profile/PaperPacketIntakePanel';
import MarkCompletePaperDialog from './student_profile/MarkCompletePaperDialog';
import IepServicesTab from './student_profile/IepServicesTab';
// Standalone print view (e.g. dedicated route); this screen uses inline HTML for "Print ID form" per product spec.
// eslint-disable-next-line no-unused-vars
import PrintIdentificationForm from './student_profile/PrintIdentificationForm';
import Fa12SocialHistoryFields, {
  buildFa12SubmitPayload,
  initialFa12FieldsFromPacket,
} from './Fa12SocialHistoryFields';
import Fa17FeedingQuestionnaireFields, { initialFa17Fields } from './Fa17FeedingQuestionnaireFields';
import { useAuth } from '../contexts/AuthContext';

const Field = ({ label, value }) => (
  <Box sx={{ mb: 1.5 }}>
    <Typography variant="caption" color="text.secondary" display="block">
      {label}
    </Typography>
    <Typography variant="body1">{value ?? '—'}</Typography>
  </Box>
);

function AddEmergencyContactButton({ studentId, onAdded }) {
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [name, setName] = useState('');
  const [relationship, setRelationship] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [order, setOrder] = useState(1);
  const handleAdd = () => {
    if (!(name || '').trim()) return;
    setSaving(true);
    studentAPI
      .createEmergencyContact(studentId, { name: name.trim(), relationship: relationship.trim(), address: address.trim(), phone: phone.trim(), order: Math.min(3, Math.max(1, order)) })
      .then(() => {
        setOpen(false);
        setName('');
        setRelationship('');
        setAddress('');
        setPhone('');
        setOrder(1);
        onAdded();
      })
      .catch((err) => alert(err.response?.data?.detail || err.message || 'Failed to add'))
      .finally(() => setSaving(false));
  };
  return (
    <>
      <Button size="small" variant="outlined" onClick={() => setOpen(true)}>Add emergency contact</Button>
      <Dialog open={open} onClose={() => !saving && setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add emergency contact</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
            <TextField size="small" fullWidth label="Name" value={name} onChange={(e) => setName(e.target.value)} required />
            <TextField size="small" fullWidth label="Relationship" value={relationship} onChange={(e) => setRelationship(e.target.value)} />
            <TextField size="small" fullWidth label="Address" value={address} onChange={(e) => setAddress(e.target.value)} multiline />
            <TextField size="small" fullWidth label="Phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
            <TextField size="small" fullWidth type="number" inputProps={{ min: 1, max: 3 }} label="Order (1–3)" value={order} onChange={(e) => setOrder(Number(e.target.value) || 1)} />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => !saving && setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleAdd} disabled={saving || !(name || '').trim()}>{saving ? 'Adding…' : 'Add'}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const formatDate = (d) => {
  if (!d) return '—';
  const s = typeof d === 'string' ? d.split('T')[0] : '';
  const [y, m, day] = s.split('-');
  return y && m && day ? `${Number(m)}/${Number(day)}/${y}` : '—';
};

const DOC_CATEGORIES = [
  { value: 'student_identification', label: 'Student identification records' },
  { value: 'evaluation', label: 'Evaluation records (referral, multidisciplinary, psych, speech, OT, PT)' },
  { value: 'eligibility', label: 'Eligibility determination (CPSE/CSE minutes, classification)' },
  { value: 'individualized_plans', label: 'Individualized plans (IFSP, IEPs, implementation)' },
  { value: 'consent', label: 'Consent forms (evaluations, services, information sharing)' },
  { value: 'service_records', label: 'Service records (provider logs, attendance, progress, therapy notes)' },
  { value: 'meeting_records', label: 'Meeting records (notices, attendance, PWN, IEP amendments)' },
  { value: 'progress_monitoring', label: 'Progress monitoring (quarterly reports, annual reviews)' },
  { value: 'transition', label: 'Transition documentation (EI→CPSE, CPSE→CSE, transition plans)' },
  { value: 'transportation', label: 'Transportation (signed TRFs)' },
  { value: 'log_notes', label: 'Log notes' },
  { value: 'log_notes_easytrac', label: 'EasyTrac log notes (uploaded PDF)' },
  { value: 'other_custom', label: 'Other / Custom' },
];

const StudentDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [student, setStudent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pdfUploading, setPdfUploading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const fileInputRef = React.useRef(null);
  const tabParam = searchParams.get('tab');
  // Tab order: Overview, Medical, Parent/Guardian, Parent Packet, Documents, Services
  const [tab, setTab] = useState(
    tabParam === 'medical'
      ? 1
      : tabParam === 'parent-guardian'
        ? 2
        : tabParam === 'parent-packet'
          ? 3
          : tabParam === 'documents'
            ? 4
            : tabParam === 'services' || tabParam === 'iep'
              ? 5
              : 0,
  );
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [docCategoryFilter, setDocCategoryFilter] = useState('');
  const [selectedDocIds, setSelectedDocIds] = useState([]);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [paperIntakeOpen, setPaperIntakeOpen] = useState(false);
  const [paperIntakeDocId, setPaperIntakeDocId] = useState(null);
  const [paperMarkOpen, setPaperMarkOpen] = useState(false);
  const [paperMarkDocId, setPaperMarkDocId] = useState(null);
  const [docUploadFile, setDocUploadFile] = useState(null);
  const [docUploadCategory, setDocUploadCategory] = useState('student_identification');
  const [docUploadCustomCategory, setDocUploadCustomCategory] = useState('');
  const [docUploadTitle, setDocUploadTitle] = useState('');
  const [docUploadDescription, setDocUploadDescription] = useState('');
  const [docUploadDateOfDocument, setDocUploadDateOfDocument] = useState(() => new Date().toISOString().slice(0, 10));
  const [docUploading, setDocUploading] = useState(false);
  const docUploadInputRef = React.useRef(null);
  const [exitPackageChecking, setExitPackageChecking] = useState(false);
  const [exitPackageMissing, setExitPackageMissing] = useState(null);
  const [exitNotifyUserId, setExitNotifyUserId] = useState('');
  const [exitNotifySending, setExitNotifySending] = useState(false);
  const [staffUsers, setStaffUsers] = useState([]);
  const [parentGuardian, setParentGuardian] = useState(null);
  const [parentGuardianLoading, setParentGuardianLoading] = useState(false);
  const [parentGuardianSaving, setParentGuardianSaving] = useState(false);
  const [parentGuardianError, setParentGuardianError] = useState(null);
  const [parentGuardianSuccess, setParentGuardianSuccess] = useState(false);
  const [pgFormData, setPgFormData] = useState(null);
  const [packetForms, setPacketForms] = useState([]);
  const [packetFormsLoading, setPacketFormsLoading] = useState(false);
  const [packetStatus, setPacketStatus] = useState(null);
  const [packetStatusLoading, setPacketStatusLoading] = useState(false);
  const [packetSendLoading, setPacketSendLoading] = useState(false);
  const [packetSendError, setPacketSendError] = useState(null);
  const [packetSendSuccess, setPacketSendSuccess] = useState(null);
  const [packetRemindLoading, setPacketRemindLoading] = useState(false);
  const [resetPacketDialogOpen, setResetPacketDialogOpen] = useState(false);
  const [resetPacketLoading, setResetPacketLoading] = useState(false);
  const [packetLinkCopied, setPacketLinkCopied] = useState(false);
  const [packetArchiveLoading, setPacketArchiveLoading] = useState(false);
  const [packetReviewedLoading, setPacketReviewedLoading] = useState(false);
  const [intakeJsonLoading, setIntakeJsonLoading] = useState(false);
  const [socialFields, setSocialFields] = useState(() => initialFa12FieldsFromPacket({}));
  const [feedingFields, setFeedingFields] = useState(() => initialFa17Fields());
  const [intakeJsonSaving, setIntakeJsonSaving] = useState(false);
  const [intakeJsonError, setIntakeJsonError] = useState(null);
  const [intakeJsonSuccess, setIntakeJsonSuccess] = useState(false);
  const setSocialF = useCallback((key, value) => {
    setSocialFields((prev) => ({ ...prev, [key]: value }));
  }, []);
  const setFeedingF = useCallback((key, value) => {
    setFeedingFields((prev) => ({ ...prev, [key]: value }));
  }, []);
  const [photoUploading, setPhotoUploading] = useState(false);
  const [photoUploadError, setPhotoUploadError] = useState('');
  const [photoVersion, setPhotoVersion] = useState(0);
  const photoInputRef = React.useRef(null);
  const photoObjectUrl = useAuthenticatedStudentPhoto(
    student?.id,
    Boolean(student?.photo_url || student?.photo),
    photoVersion,
  );

  const PHOTO_ACCEPT = 'image/jpeg,image/png,image/webp,image/gif,.jpg,.jpeg,.png,.webp,.gif';

  const photoErrorMessage = (err) => {
    const data = err?.response?.data;
    if (typeof data?.detail === 'string') return data.detail;
    if (Array.isArray(data?.detail)) return data.detail.join(' ');
    return err?.message || 'Upload failed';
  };

  const applyStudentPhotoFromResponse = (data) => {
    setStudent((prev) => (prev ? { ...prev, photo_url: data.photo_url ?? null, photo: data.photo ?? null } : prev));
    if (data.photo_url) setPhotoVersion((v) => v + 1);
  };

  useEffect(() => {
    let cancelled = false;
    studentAPI
      .getStudent(id)
      .then((res) => {
        if (!cancelled) setStudent(res.data);
      })
      .catch(() => {
        if (!cancelled) setStudent(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [id]);

  useEffect(() => {
    if (tabParam === 'medical') setTab(1);
    if (tabParam === 'parent-guardian') setTab(2);
    if (tabParam === 'parent-packet') setTab(3);
    if (tabParam === 'documents') setTab(4);
  }, [tabParam]);

  useEffect(() => {
    if (tab !== 3 || !student?.id) return;
    setPacketFormsLoading(true);
    studentAPI.getParentPacketForms(student.id)
      .then((res) => setPacketForms(res.data?.forms || []))
      .catch(() => setPacketForms([]))
      .finally(() => setPacketFormsLoading(false));
  }, [tab, student?.id]);

  const refreshPacketStatus = useCallback(() => {
    if (!student?.id) return;
    setPacketStatusLoading(true);
    getParentPacketStatus(student.id)
      .then((res) => setPacketStatus(res.data))
      .catch(() => setPacketStatus(null))
      .finally(() => setPacketStatusLoading(false));
  }, [student?.id]);

  // Parent/Guardian (tab 2) renders Print signed copy from packetStatus — must fetch there too,
  // not only on Parent Packet (tab 3). Otherwise status stays null → "unavailable".
  useEffect(() => {
    if ((tab !== 2 && tab !== 3) || !student?.id) return;
    refreshPacketStatus();
  }, [tab, student?.id, refreshPacketStatus]);

  useEffect(() => {
    if (tab !== 4 || !student?.id) return;
    let cancelled = false;
    setIntakeJsonLoading(true);
    setIntakeJsonError(null);
    Promise.all([
      studentAPI.getStudentSocialHistory(student.id).catch(() => ({ data: { data: {} } })),
      studentAPI.getStudentFeedingQuestionnaire(student.id).catch(() => ({ data: { data: {} } })),
    ])
      .then(([sh, fq]) => {
        if (cancelled) return;
        setSocialFields(initialFa12FieldsFromPacket(sh.data?.data ?? {}));
        setFeedingFields({ ...initialFa17Fields(), ...(fq.data?.data || {}) });
      })
      .finally(() => {
        if (!cancelled) setIntakeJsonLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [tab, student?.id]);

  useEffect(() => {
    if (tab !== 2 || !id) return;
    let cancelled = false;
    setParentGuardianLoading(true);
    setParentGuardianError(null);
    studentAPI
      .getParentGuardian(id)
      .then((res) => {
        if (!cancelled) setParentGuardian(res.data);
      })
      .catch((err) => {
        if (!cancelled) {
          setParentGuardian(null);
          setParentGuardianError(err.response?.data?.detail || err.message || 'Failed to load');
        }
      })
      .finally(() => {
        if (!cancelled) setParentGuardianLoading(false);
      });
    return () => { cancelled = true; };
  }, [tab, id]);

  useEffect(() => {
    if (parentGuardian && tab === 2) {
      const copy = { ...parentGuardian };
      delete copy.emergency_contacts;
      setPgFormData(copy);
    } else if (tab !== 2) {
      setPgFormData(null);
    }
  }, [parentGuardian, tab]);

  const handleSaveParentGuardian = () => {
    if (!id || !pgFormData) return;
    setParentGuardianError(null);
    setParentGuardianSuccess(false);
    setParentGuardianSaving(true);
    const payload = {
      mother_name: pgFormData.mother_name ?? '',
      mother_cell_phone: pgFormData.mother_cell_phone ?? '',
      mother_work_address: pgFormData.mother_work_address ?? '',
      mother_work_phone: pgFormData.mother_work_phone ?? '',
      father_name: pgFormData.father_name ?? '',
      father_cell_phone: pgFormData.father_cell_phone ?? '',
      father_work_address: pgFormData.father_work_address ?? '',
      father_work_phone: pgFormData.father_work_phone ?? '',
      home_address: pgFormData.home_address ?? '',
      preferred_phone: pgFormData.preferred_phone ?? '',
      email: pgFormData.email ?? '',
      email_viewed_daily: !!pgFormData.email_viewed_daily,
      pediatrician_name: pgFormData.pediatrician_name ?? '',
      pediatrician_phone: pgFormData.pediatrician_phone ?? '',
      dentist_name: pgFormData.dentist_name ?? '',
      dentist_phone: pgFormData.dentist_phone ?? '',
      specialist_name: pgFormData.specialist_name ?? '',
      specialist_specialty: pgFormData.specialist_specialty ?? '',
      specialist_phone: pgFormData.specialist_phone ?? '',
      preferred_hospital: pgFormData.preferred_hospital ?? '',
      child_insurance_id: pgFormData.child_insurance_id ?? '',
      insurance_type: pgFormData.insurance_type ?? '',
    };
    studentAPI
      .updateParentGuardian(id, payload)
      .then((res) => {
        setParentGuardian(res.data);
        setPgFormData((prev) => (prev ? { ...prev, ...res.data } : null));
        setParentGuardianSuccess(true);
        setTimeout(() => setParentGuardianSuccess(false), 3000);
      })
      .catch((err) => {
        setParentGuardianError(err.response?.data?.detail || (err.response?.data && JSON.stringify(err.response.data)) || err.message || 'Save failed');
      })
      .finally(() => setParentGuardianSaving(false));
  };

  const prefilledSet = new Set(parentGuardian?.prefilled_from_student || []);

  useEffect(() => {
    if (tab !== 4 || !id) return;
    let cancelled = false;
    setDocumentsLoading(true);
    studentAPI
      .getDocuments(id, docCategoryFilter ? { category: docCategoryFilter } : {})
      .then((res) => {
        if (!cancelled) setDocuments(Array.isArray(res.data) ? res.data : res.data?.results || []);
      })
      .catch(() => {
        if (!cancelled) setDocuments([]);
      })
      .finally(() => {
        if (!cancelled) setDocumentsLoading(false);
      });
    return () => { cancelled = true; };
  }, [tab, id, docCategoryFilter]);

  useEffect(() => {
    if (exitPackageMissing && staffUsers.length === 0) {
      userManagementAPI.getUsers().then((res) => {
        const list = res.data?.results || res.data || [];
        setStaffUsers(Array.isArray(list) ? list : []);
      }).catch(() => setStaffUsers([]));
    }
  }, [exitPackageMissing, staffUsers.length]);

  const canEdit = user?.role === 'admin' || user?.role === 'editor' || user?.role === 'superadmin';
  const canAdminPacket = user?.role === 'admin' || user?.role === 'superadmin';
  const canArchive = user?.role === 'admin' || user?.role === 'superadmin';

  const handleArchiveToggle = async () => {
    if (!student?.id) return;
    const archiving = student.is_active !== false;
    const msg = archiving
      ? `Archive ${student.first_name} ${student.last_name}? The student is hidden from the default search but nothing is deleted; you can restore at any time.`
      : `Restore ${student.first_name} ${student.last_name} to the active list?`;
    if (!window.confirm(msg)) return;
    try {
      const res = archiving
        ? await studentAPI.archiveStudent(student.id)
        : await studentAPI.restoreStudent(student.id);
      setStudent((prev) => (prev ? { ...prev, is_active: res.data.is_active, status: res.data.status } : prev));
    } catch (e) {
      window.alert(e.response?.data?.detail || 'Archive/restore failed.');
    }
  };
  const packetSendEligible = (student?.service_type || '') === 'center_based';
  const isFloater = (student?.last_name || '').toLowerCase() === 'floater';

  const handleViewPdf = () => {
    setPdfError(null);
    studentAPI.openPdf(id).catch((err) => {
      setPdfError(err.message || 'Could not open PDF');
    });
  };

  const handleUploadPdf = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setPdfError('Please select a PDF file.');
      return;
    }
    setPdfError(null);
    setPdfUploading(true);
    studentAPI
      .uploadPdf(id, file)
      .then((res) => {
        setStudent(res.data);
        if (fileInputRef.current) fileInputRef.current.value = '';
      })
      .catch((err) => {
        setPdfError(err.response?.data?.detail || err.message || 'Upload failed');
      })
      .finally(() => setPdfUploading(false));
  };

  const handleRemovePdf = () => {
    if (!window.confirm('Remove the attached PDF from this record?')) return;
    setPdfError(null);
    studentAPI
      .deletePdf(id)
      .then(() => setStudent((s) => (s ? { ...s, uploaded_pdf_url: null } : null)))
      .catch((err) => setPdfError(err.response?.data?.detail || err.message || 'Remove failed'));
  };

  const refreshDocuments = () => {
    if (tab !== 4) return;
    studentAPI
      .getDocuments(id, docCategoryFilter ? { category: docCategoryFilter } : {})
      .then((res) => setDocuments(Array.isArray(res.data) ? res.data : res.data?.results || []))
      .catch(() => setDocuments([]));
  };

  const handleDocUpload = () => {
    if (!docUploadFile) return;
    if (docUploadCategory === 'other_custom' && !(docUploadCustomCategory || '').trim()) return;
    setDocUploading(true);
    studentAPI
      .uploadDocument(id, docUploadFile, docUploadCategory, docUploadTitle, {
        description: docUploadDescription || undefined,
        date_of_document: docUploadDateOfDocument || undefined,
        custom_category: docUploadCategory === 'other_custom' ? docUploadCustomCategory : undefined,
      })
      .then(() => {
        setDocUploadFile(null);
        setDocUploadTitle('');
        setDocUploadDescription('');
        setDocUploadDateOfDocument(new Date().toISOString().slice(0, 10));
        setDocUploadCustomCategory('');
        setUploadModalOpen(false);
        if (docUploadInputRef.current) docUploadInputRef.current.value = '';
        refreshDocuments();
      })
      .catch((err) => {
        alert(err.response?.data?.detail || err.response?.data?.custom_category?.[0] || err.message || 'Upload failed');
      })
      .finally(() => setDocUploading(false));
  };

  const handleGenerateExitPackage = () => {
    setExitPackageChecking(true);
    setExitPackageMissing(null);
    studentAPI.exitPackageCheck(id)
      .then((res) => {
        if (res.data.ready) {
          return studentAPI.exitPackageGenerate(id);
        }
        setExitPackageMissing(res.data.missing_categories || []);
      })
      .catch((err) => {
        alert(err.response?.data?.detail || err.message || 'Failed');
      })
      .finally(() => setExitPackageChecking(false));
  };

  const handleExitNotify = (channel) => {
    if (!exitNotifyUserId) {
      alert('Select a staff member.');
      return;
    }
    setExitNotifySending(true);
    studentAPI.exitPackageNotify(id, Number(exitNotifyUserId), channel)
      .then((res) => {
        alert(res.data?.detail || 'Sent.');
        setExitPackageMissing(null);
      })
      .catch((err) => {
        alert(err.response?.data?.detail || err.message || 'Failed to send');
      })
      .finally(() => setExitNotifySending(false));
  };

  const handleDeleteDoc = (docId) => {
    if (!window.confirm('Remove this document from the student record?')) return;
    studentAPI.deleteDocument(id, docId).then(() => {
      setSelectedDocIds((prev) => prev.filter((d) => d !== docId));
      refreshDocuments();
    });
  };

  const handleDownloadPackage = () => {
    if (selectedDocIds.length === 0) return;
    studentAPI.downloadDocumentPackage(id, selectedDocIds).catch((err) => {
      alert(err.response?.data?.detail || err.message || 'Download failed');
    });
  };

  const toggleDocSelection = (docId) => {
    setSelectedDocIds((prev) =>
      prev.includes(docId) ? prev.filter((d) => d !== docId) : [...prev, docId]
    );
  };

  const toggleSelectAllDocs = () => {
    if (selectedDocIds.length >= documents.length) {
      setSelectedDocIds([]);
    } else {
      setSelectedDocIds(documents.map((d) => d.id));
    }
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowed = /\.(jpe?g|png|gif|webp)$/i;
    if (!allowed.test(file.name || '')) {
      setPhotoUploadError('Please choose a JPG, PNG, WebP, or GIF image.');
      if (photoInputRef.current) photoInputRef.current.value = '';
      return;
    }
    setPhotoUploadError('');
    setPhotoUploading(true);
    studentAPI.uploadPhoto(id, file)
      .then((res) => {
        applyStudentPhotoFromResponse(res.data);
        if (photoInputRef.current) photoInputRef.current.value = '';
      })
      .catch((err) => setPhotoUploadError(photoErrorMessage(err)))
      .finally(() => setPhotoUploading(false));
  };

  const handlePhotoRemove = () => {
    if (!window.confirm('Remove the child photo?')) return;
    setPhotoUploadError('');
    setPhotoUploading(true);
    studentAPI.deletePhoto(id)
      .then((res) => {
        applyStudentPhotoFromResponse(res.data);
        setPhotoVersion(0);
      })
      .catch((err) => setPhotoUploadError(photoErrorMessage(err)))
      .finally(() => setPhotoUploading(false));
  };

  const handlePacketFormPdf = (formId) => {
    if (!student?.id) {
      alert('Student record is still loading. Try again in a moment.');
      return;
    }
    studentAPI.getParentPacketFormPdf(student.id, formId)
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `parent_packet_${formId}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((err) => alert(err.response?.data?.detail || err.message || 'Failed'));
  };

  const handlePacketPreviewAll = () => {
    if (!student?.id) {
      alert('Student record is still loading. Try again in a moment.');
      return;
    }
    studentAPI.getParentPacketPreview(student.id)
      .then((res) => {
        const url = URL.createObjectURL(res.data);
        const a = document.createElement('a');
        a.href = url;
        a.download = `parent_packet_preview_${student.id}.pdf`;
        a.click();
        URL.revokeObjectURL(url);
      })
      .catch((err) => alert(err.response?.data?.detail || err.message || 'Failed'));
  };

  const handleSendPacket = () => {
    if (!student?.id) {
      setPacketSendError('Student record is still loading.');
      return;
    }
    setPacketSendError(null);
    setPacketSendSuccess(null);
    setPacketSendLoading(true);
    sendParentPacket(student.id)
      .then((res) => {
        setPacketSendSuccess(res.data?.packet_url ? 'Packet sent successfully.' : 'Sent.');
        refreshPacketStatus();
        studentAPI.getParentPacketForms(student.id).then((r) => setPacketForms(r.data?.forms || []));
      })
      .catch((err) => {
        const msg = err.response?.data?.detail || err.message || 'Failed to send packet.';
        setPacketSendError(msg);
      })
      .finally(() => setPacketSendLoading(false));
  };

  const handleRemindPacket = () => {
    if (!student?.id) return;
    setPacketRemindLoading(true);
    remindParentPacket(student.id)
      .then(() => refreshPacketStatus())
      .catch((err) => alert(err.response?.data?.detail || err.message || 'Failed to send reminder.'))
      .finally(() => setPacketRemindLoading(false));
  };

  const handleConfirmResetPacket = () => {
    if (!student?.id) return;
    setResetPacketLoading(true);
    resetParentPacket(student.id)
      .then(() => {
        setResetPacketDialogOpen(false);
        refreshPacketStatus();
        studentAPI.getParentPacketForms(student.id).then((r) => setPacketForms(r.data?.forms || []));
        setPacketSendSuccess('Packet reset. Use Send to issue a new link.');
      })
      .catch((err) => alert(err.response?.data?.detail || err.message || 'Failed to reset packet.'))
      .finally(() => setResetPacketLoading(false));
  };

  const handleCopyPacketLink = () => {
    const url = packetStatus?.packet?.packet_url;
    if (url && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(url).then(() => {
        setPacketLinkCopied(true);
        setTimeout(() => setPacketLinkCopied(false), 2000);
      });
    }
  };

  const handleDownloadPacketArchive = () => {
    if (!student?.id) return;
    const { signedCopySource: source } = resolveSignedCopyUi(packetStatus);
    if (!source) return;
    setPacketArchiveLoading(true);
    studentAPI
      .downloadParentPacketArchive(student.id)
      .catch((err) => alert(err.response?.data?.detail || err.message || 'Download failed.'))
      .finally(() => setPacketArchiveLoading(false));
  };

  const { signedCopySource, hasDocusealSignedCopy } = resolveSignedCopyUi(packetStatus);

  const handleSaveIntakeForms = () => {
    if (!student?.id || !canEdit) return;
    setIntakeJsonError(null);
    const socialObj = buildFa12SubmitPayload(socialFields);
    const feedObj = { ...feedingFields };
    setIntakeJsonSaving(true);
    studentAPI
      .updateStudentSocialHistory(student.id, { data: socialObj })
      .then(() => studentAPI.updateStudentFeedingQuestionnaire(student.id, { data: feedObj }))
      .then(() => {
        setIntakeJsonSuccess(true);
        setTimeout(() => setIntakeJsonSuccess(false), 3000);
      })
      .catch((err) => {
        setIntakeJsonError(err.response?.data?.detail || err.message || 'Save failed.');
      })
      .finally(() => setIntakeJsonSaving(false));
  };

  if (loading) {
    return (
      <Box>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Typography>Loading…</Typography>
        </Container>
      </Box>
    );
  }

  if (!student) {
    return (
      <Box>
        <Container maxWidth="md" sx={{ py: 4 }}>
          <Typography color="error">Student not found.</Typography>
          <Button startIcon={<ArrowBack />} onClick={() => navigate('/students')} sx={{ mt: 2 }}>
            Back to Search
          </Button>
        </Container>
      </Box>
    );
  }

  const districtDisplay = student.district_display || student.district || student.school_district || '—';

  return (
    <Box>
      <Paper elevation={0} sx={{ px: 2, py: 1.5, mb: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/students')}>
          Back to Search
        </Button>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
          {student.is_active === false && (
            <Chip label="Archived" color="warning" size="small" />
          )}
          {canArchive && (
            <Button
              variant="outlined"
              color={student.is_active === false ? 'success' : 'warning'}
              size="small"
              onClick={handleArchiveToggle}
            >
              {student.is_active === false ? 'Restore' : 'Archive'}
            </Button>
          )}
          {canEdit && (
            <>
              <Button
                variant="contained"
                startIcon={<Edit />}
                onClick={() => navigate(`/students/${id}/edit`)}
              >
                Edit
              </Button>
              <Button
                variant="outlined"
                size="small"
                startIcon={<PrintIcon />}
                onClick={() => {
                  const printWindow = window.open('', '_blank');
                  if (printWindow) {
                    printWindow.document.title = `ID Form - ${student.first_name} ${student.last_name}`;
                    const root = printWindow.document.createElement('div');
                    root.id = 'print-root';
                    printWindow.document.body.appendChild(root);
                    const style = printWindow.document.createElement('style');
                    style.textContent = 'body { font-family: Arial, sans-serif; margin: 20px; } @media print { body { margin: 0; } }';
                    printWindow.document.head.appendChild(style);
                    const name = `${student.first_name || ''} ${student.last_name || ''}`;
                    const fields = [
                      ['Name', name.trim()],
                      ['Nickname', student.nickname],
                      ['Date of Birth', student.date_of_birth],
                      ['Hair Color', student.hair_color],
                      ['Eye Color', student.eye_color],
                      ['Glasses', student.glasses],
                      ['Physical Description', student.physical_description],
                      ['Usual Behavior', student.usual_behavior],
                      ['Communication Ability', student.communication_ability],
                      ['Favorite Locations', student.favorite_locations],
                    ];
                    let html = '<h2 style="text-align:center">CHILD IDENTIFICATION FORM</h2>';
                    html += '<p style="text-align:center;color:#666">HASC / Gan Ezra — Emergency Identification</p><hr/>';
                    const photoSrc = photoObjectUrl;
                    if (photoSrc) {
                      html += `<div style="text-align:center;margin:10px 0"><img src="${String(photoSrc).replace(/"/g, '&quot;')}" style="max-height:200px;max-width:200px;object-fit:cover;border:1px solid #ccc" /></div>`;
                    }
                    fields.forEach(([label, val]) => {
                      html += `<p><strong>${label}:</strong> ${(val != null && val !== '') ? String(val).replace(/</g, '&lt;') : '—'}</p>`;
                    });
                    const summary = student.medical_alert_summary;
                    if (summary && summary.has_alerts) {
                      html += '<hr/><p><strong>Medical Alerts</strong></p>';
                      (summary.allergies || []).forEach((a) => {
                        html += `<p>• Allergy: ${a.allergen}${a.severity ? ` (${a.severity})` : ''}</p>`;
                      });
                      (summary.conditions || []).forEach((c) => {
                        html += `<p>• ${String(c).replace(/</g, '&lt;')}</p>`;
                      });
                      if (summary.has_action_plan) html += '<p><strong>⚠ Emergency action plan on file</strong></p>';
                    }
                    root.innerHTML = html;
                    setTimeout(() => printWindow.print(), 300);
                  }
                }}
              >
                Print ID form
              </Button>
            </>
          )}
        </Box>
      </Paper>

      <Container maxWidth="md" sx={{ pb: 4 }}>
        {student.duplicate_warning && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            Possible duplicate record detected. Another student in this session has the same name and DOB.
          </Alert>
        )}
        {isFloater && (
          <Alert severity="info" sx={{ mb: 2 }}>
            This record is marked as a non-student placeholder (floater).
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2, flexWrap: 'wrap' }}>
          <Box>
            <Typography variant="h5" gutterBottom>
              {student.first_name} {student.last_name}
            </Typography>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Session: {student.session_name} · Student ID: {student.id}
            </Typography>
            <Chip label={student.status} size="small" sx={{ mt: 1 }} />
          </Box>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              {photoObjectUrl ? (
                <Box
                  component="img"
                  src={photoObjectUrl}
                  alt="Child"
                  sx={{ width: 64, height: 80, objectFit: 'cover', borderRadius: 1, border: '1px solid', borderColor: 'divider' }}
                />
              ) : (
                <Box sx={{ width: 64, height: 80, border: '1px dashed', borderColor: 'divider', borderRadius: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <PhotoCamera sx={{ color: 'text.disabled' }} />
                </Box>
              )}
              {canEdit && (
                <>
                  <Button size="small" variant="outlined" startIcon={photoUploading ? <CircularProgress size={16} /> : <Upload />} onClick={() => photoInputRef.current?.click()} disabled={photoUploading}>
                    {student.photo_url ? 'Replace' : 'Upload'} photo
                  </Button>
                  {student.photo_url && (
                    <Button size="small" color="error" onClick={handlePhotoRemove} disabled={photoUploading}>Remove</Button>
                  )}
                  <input ref={photoInputRef} type="file" accept={PHOTO_ACCEPT} onChange={handlePhotoUpload} style={{ display: 'none' }} />
                </>
              )}
            </Box>
            {photoUploadError && (
              <Alert severity="error" onClose={() => setPhotoUploadError('')} sx={{ maxWidth: 420 }}>
                {photoUploadError}
              </Alert>
            )}
          </Box>
        </Box>

        {user?.role === 'transportation' && (
          <Paper sx={{ p: 2, mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <DirectionsBus /> Transportation (TRF)
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate(`/transportation/${id}`)}
            >
              Open TRF / Print or export PDF
            </Button>
          </Paper>
        )}

        {(student.status === 'aged_out' || student.status === 'graduated') && (
          <Paper sx={{ p: 2, mt: 2, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <ExitToApp /> Exit Package
            </Typography>
            <Button
              variant="contained"
              size="small"
              disabled={exitPackageChecking}
              onClick={handleGenerateExitPackage}
            >
              {exitPackageChecking ? 'Checking…' : 'Generate Exit Package'}
            </Button>
          </Paper>
        )}

        <MedicalAlertBanner
          summary={student?.medical_alert_summary}
          onActionPlanClick={() => setTab(1)}
        />

        <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mt: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Tab label="Overview" id="student-tab-0" />
          <Tab label="Medical & Safety" id="student-tab-1" />
          <Tab label="Parent / Guardian" id="student-tab-2" />
          <Tab label="Parent Packet" id="student-tab-3" />
          <Tab label="Documents" id="student-tab-4" />
          <Tab label="Services / IEP" id="student-tab-5" sx={{ display: user?.role === 'nurse' ? 'none' : undefined }} />
        </Tabs>

        {tab === 0 && (
        <>
        {/* A) Personal / Demographics */}
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" color="primary" gutterBottom>Personal / Demographics</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}><Field label="First name" value={student.first_name} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Last name" value={student.last_name} /></Grid>
            <Grid item xs={12} sm={6}><Field label="DOB" value={formatDate(student.date_of_birth)} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Status" value={student.status} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Service type" value={student.service_type} /></Grid>
            <Grid item xs={12}><Field label="Address" value={student.address} /></Grid>
          </Grid>
        </Paper>

        {/* B) Parent / Guardian (from ParentGuardian record) */}
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" color="primary" gutterBottom>Parent / Guardian</Typography>
          <Grid container spacing={3}>
            {(() => {
              const pg = student.parent_guardian_overview || {};
              return (
                <>
                  <Grid item xs={12} sm={6}><Field label="Mother name" value={pg.mother_name} /></Grid>
                  <Grid item xs={12} sm={6}><Field label="Mother cell" value={pg.mother_cell_phone} /></Grid>
                  <Grid item xs={12} sm={6}><Field label="Father name" value={pg.father_name} /></Grid>
                  <Grid item xs={12} sm={6}><Field label="Father cell" value={pg.father_cell_phone} /></Grid>
                  <Grid item xs={12}><Field label="Home address" value={pg.home_address} /></Grid>
                  <Grid item xs={12} sm={6}><Field label="Email" value={pg.email} /></Grid>
                  <Grid item xs={12}><Field label="Emergency contact (first)" value={pg.emergency_contact_first} /></Grid>
                  <Grid item xs={12} sm={6}><Field label="Pediatrician name" value={pg.pediatrician_name} /></Grid>
                  <Grid item xs={12} sm={6}><Field label="Pediatrician phone" value={pg.pediatrician_phone} /></Grid>
                </>
              );
            })()}
          </Grid>
        </Paper>

        {/* C) Program / Roster */}
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" color="primary" gutterBottom>Program / Roster</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}><Field label="Class" value={student.class_num} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Funding code" value={student.funding_code} /></Grid>
            <Grid item xs={12} sm={6}><Field label="District" value={districtDisplay} /></Grid>
            <Grid item xs={12} sm={6}><Field label="School district" value={student.school_district} /></Grid>
            <Grid item xs={12} sm={6}><Field label="1:1 Aide" value={student.aide_1to1} /></Grid>
          </Grid>
        </Paper>

        {/* D) Discharge */}
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" color="primary" gutterBottom>Discharge</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}><Field label="Discharge date" value={formatDate(student.discharge_date)} /></Grid>
            <Grid item xs={12}><Field label="Discharge notes" value={student.discharge_notes} /></Grid>
          </Grid>
        </Paper>

        {/* E) Vaccines / Medical (consolidated) */}
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" color="primary" gutterBottom>Vaccines / Medical</Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}><Field label="Vaccine status" value={student.vaccines_status} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Medical due date" value={formatDate(student.medical_due_date)} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Vaccines last reviewed" value={formatDate(student.vaccines_last_reviewed)} /></Grid>
            <Grid item xs={12} sm={6}><Field label="Medical start / end" value={[student.medical_start_date, student.medical_end_date].filter(Boolean).map(formatDate).join(' – ') || '—'} /></Grid>
            <Grid item xs={12}><Field label="Vaccines notes" value={student.vaccines_notes} /></Grid>
            {(student.medical_info != null && student.medical_info !== '') && (
              <Grid item xs={12}><Field label="Nurses medical notes (restricted)" value="*** on file ***" /></Grid>
            )}
          </Grid>
        </Paper>

        {user?.role !== 'attendance' && (
          <TherapyPrescriptionsCard studentId={student?.id} canEdit={canEdit} />
        )}

        {/* C) SPED / Program / IFSP-IEP */}
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            SPED / Program / IFSP-IEP
          </Typography>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}><Field label="SPED INDIV Code" value={student.sped_indiv_code} /></Grid>
          </Grid>
        </Paper>

        {/* D) Services */}
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Services
          </Typography>
          {Array.isArray(student.services) && student.services.length > 0 ? (
            <TableContainer><Table size="small">
              <TableHead><TableRow><TableCell>Service</TableCell><TableCell>Provider</TableCell><TableCell>Start</TableCell><TableCell>Due</TableCell></TableRow></TableHead>
              <TableBody>
                {student.services.map((svc, i) => (
                  <TableRow key={i}>
                    <TableCell>{svc.service_type}</TableCell>
                    <TableCell>{svc.provider}</TableCell>
                    <TableCell>{formatDate(svc.start_date)}</TableCell>
                    <TableCell>{formatDate(svc.due_date)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">No services recorded.</Typography>
          )}
        </Paper>

        {/* E) Reporting */}
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Reporting
          </Typography>
          {student.reports && typeof student.reports === 'object' && Object.keys(student.reports).length > 0 ? (
            <Typography variant="body2">{JSON.stringify(student.reports)}</Typography>
          ) : (
            <Typography variant="body2" color="text.secondary">No report data.</Typography>
          )}
        </Paper>

        {/* F) Meetings */}
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Meetings
          </Typography>
          {Array.isArray(student.meetings) && student.meetings.length > 0 ? (
            <TableContainer><Table size="small">
              <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Time</TableCell><TableCell>Place</TableCell><TableCell>Packet sent</TableCell><TableCell>Packet received</TableCell></TableRow></TableHead>
              <TableBody>
                {student.meetings.map((m, i) => (
                  <TableRow key={i}>
                    <TableCell>{formatDate(m.date)}</TableCell>
                    <TableCell>{m.time}</TableCell>
                    <TableCell>{m.place}</TableCell>
                    <TableCell>{m.packet_sent ? 'Yes' : '—'}</TableCell>
                    <TableCell>{m.packet_received ? 'Yes' : '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></TableContainer>
          ) : (
            <Typography variant="body2" color="text.secondary">No meetings recorded.</Typography>
          )}
        </Paper>

        {/* G) Notes + Incidents */}
        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Notes
          </Typography>
          <Field label="Notes (general)" value={student.notes} />
          {student.phi_encrypted && (
            <Typography variant="caption" color="text.secondary" display="block">PHI/PII on file (restricted)</Typography>
          )}
        </Paper>

        {Array.isArray(student.incidents) && student.incidents.length > 0 && (
          <Paper sx={{ p: 3, mt: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Incidents
            </Typography>
            <TableContainer><Table size="small">
              <TableHead><TableRow><TableCell>Date</TableCell><TableCell>Time</TableCell><TableCell>Location</TableCell><TableCell>Type</TableCell><TableCell>Description</TableCell><TableCell>Status</TableCell></TableRow></TableHead>
              <TableBody>
                {student.incidents.map((inc) => (
                  <TableRow key={inc.id}>
                    <TableCell>{inc.incident_date}</TableCell>
                    <TableCell>{inc.incident_time || '—'}</TableCell>
                    <TableCell>{inc.location || '—'}</TableCell>
                    <TableCell>{inc.incident_type || '—'}</TableCell>
                    <TableCell>{(inc.description || '').slice(0, 80)}{(inc.description || '').length > 80 ? '…' : ''}</TableCell>
                    <TableCell>{inc.status}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table></TableContainer>
          </Paper>
        )}

        {/* Emergency Identification */}
        <Typography variant="h6" sx={{ mt: 3, mb: 1.5, fontWeight: 700, fontSize: '1rem' }}>
          Emergency Identification
        </Typography>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="body2"><strong>Glasses:</strong> {student.glasses || '—'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2"><strong>Physical Description:</strong> {student.physical_description || '—'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2"><strong>Usual Behavior:</strong> {student.usual_behavior || '—'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2"><strong>Communication Ability:</strong> {student.communication_ability || '—'}</Typography>
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2"><strong>Favorite Locations:</strong> {student.favorite_locations || '—'}</Typography>
            </Grid>
          </Grid>
        </Paper>

        <Paper sx={{ p: 3, mt: 2 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            Attached PDF
          </Typography>
          {student.uploaded_pdf_url ? (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Button variant="contained" startIcon={<PictureAsPdf />} onClick={handleViewPdf} size="small">Open PDF</Button>
              {canEdit && (
                <>
                  <Button variant="outlined" startIcon={<Upload />} onClick={() => fileInputRef.current?.click()} disabled={pdfUploading} size="small">{pdfUploading ? 'Uploading…' : 'Replace PDF'}</Button>
                  <Button variant="outlined" color="error" startIcon={<Delete />} onClick={handleRemovePdf} size="small">Remove PDF</Button>
                </>
              )}
            </Box>
          ) : (
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">No PDF attached.</Typography>
              {canEdit && (
                <Button variant="outlined" startIcon={pdfUploading ? <CircularProgress size={16} /> : <Upload />} onClick={() => fileInputRef.current?.click()} disabled={pdfUploading} size="small">{pdfUploading ? 'Uploading…' : 'Upload PDF'}</Button>
              )}
            </Box>
          )}
          {canEdit && <input ref={fileInputRef} type="file" accept=".pdf,application/pdf" onChange={handleUploadPdf} style={{ display: 'none' }} />}
          {pdfError && <Typography variant="body2" color="error" sx={{ mt: 1 }}>{pdfError}</Typography>}
        </Paper>

        </>
        )}

        {tab === 1 && (
          <Box>
            {canEdit && (
              <Box sx={{ mb: 2, display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setPaperIntakeDocId(null);
                    setPaperIntakeOpen(true);
                  }}
                >
                  Paper packet intake
                </Button>
              </Box>
            )}
            <MedicalSafetyTab studentId={student?.id} canEdit={canEdit} />
            <NurseChart studentId={student?.id} />
          </Box>
        )}

        {tab === 4 && (
          <Paper sx={{ p: 3, mt: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              <Description /> Legal / Audit documentation
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Upload and tag documents by category. Filter by category and select documents to build a documentation package for legal or audit requests.
              EasyTrac log notes are a PDF upload only.
            </Typography>
            <Button size="small" variant="outlined" sx={{ mb: 2, mr: 1 }} onClick={() => { setDocUploadCategory('log_notes_easytrac'); setUploadModalOpen(true); }}>
              Upload EasyTrac log notes
            </Button>
            <FormControl size="small" sx={{ minWidth: 280, mb: 2 }}>
              <InputLabel>Filter by category</InputLabel>
              <Select
                value={docCategoryFilter}
                label="Filter by category"
                onChange={(e) => setDocCategoryFilter(e.target.value)}
              >
                <MenuItem value="">All categories</MenuItem>
                {DOC_CATEGORIES.map((c) => (
                  <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {canEdit && (
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" size="small" startIcon={<Upload />} onClick={() => setUploadModalOpen(true)}>
                  Upload document
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => {
                    setPaperIntakeDocId(null);
                    setPaperIntakeOpen(true);
                  }}
                >
                  Paper packet intake
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  color="success"
                  onClick={() => {
                    setPaperMarkDocId(null);
                    setPaperMarkOpen(true);
                  }}
                >
                  Mark complete (paper submission)
                </Button>
              </Box>
            )}
            {selectedDocIds.length > 0 && (
              <Box sx={{ mb: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button
                  variant="contained"
                  startIcon={<DocsPrintIcon />}
                  size="small"
                  onClick={() => studentAPI.printToPdf(id, selectedDocIds).catch((e) => alert(e.response?.data?.detail || e.message || 'Failed'))}
                >
                  Print to PDF ({selectedDocIds.length} selected)
                </Button>
                <Button
                  variant="outlined"
                  startIcon={<GetApp />}
                  size="small"
                  onClick={handleDownloadPackage}
                >
                  Download package (ZIP)
                </Button>
              </Box>
            )}
            {documentsLoading ? (
              <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
            ) : (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell padding="checkbox">
                        <Checkbox
                          indeterminate={selectedDocIds.length > 0 && selectedDocIds.length < documents.length}
                          checked={documents.length > 0 && selectedDocIds.length === documents.length}
                          onChange={toggleSelectAllDocs}
                        />
                      </TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell>Title</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell>Date of document</TableCell>
                      <TableCell>Date uploaded</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {documents.map((doc) => (
                      <TableRow key={doc.id}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            checked={selectedDocIds.includes(doc.id)}
                            onChange={() => toggleDocSelection(doc.id)}
                          />
                        </TableCell>
                        <TableCell>{doc.category_display || doc.category}</TableCell>
                        <TableCell>{doc.title || (doc.file && doc.file.split('/').pop()) || '—'}</TableCell>
                        <TableCell>{doc.description ? (doc.description.slice(0, 60) + (doc.description.length > 60 ? '…' : '')) : '—'}</TableCell>
                        <TableCell>{doc.date_of_document ? formatDate(doc.date_of_document) : '—'}</TableCell>
                        <TableCell>{doc.uploaded_at ? formatDate(doc.uploaded_at) : '—'}</TableCell>
                        <TableCell>
                          <Button size="small" onClick={() => studentAPI.downloadDocument(id, doc.id, doc.title || doc.file)}>
                            Download
                          </Button>
                          {canEdit && (
                            <Button
                              size="small"
                              onClick={() => {
                                setPaperIntakeDocId(doc.id);
                                setPaperIntakeOpen(true);
                              }}
                            >
                              Intake
                            </Button>
                          )}
                          {canEdit && (
                            <Button size="small" color="error" onClick={() => handleDeleteDoc(doc.id)}>Remove</Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
            {!documentsLoading && documents.length === 0 && (
              <Typography color="text.secondary" sx={{ py: 2 }}>No documents. Upload files and tag by category above.</Typography>
            )}

            <Typography variant="h6" color="primary" sx={{ mt: 4 }} gutterBottom>
              Social History
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Staff editor for the social-history intake stored on this student (used by parent-packet forms).
            </Typography>
            <Typography variant="h6" color="primary" sx={{ mt: 2 }} gutterBottom>
              Feeding Questionnaire
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Staff editor for the feeding questionnaire intake stored on this student.
            </Typography>
            {intakeJsonLoading && (
              <Box sx={{ py: 1 }}>
                <CircularProgress size={24} />
              </Box>
            )}
            {intakeJsonError && (
              <Alert severity="error" sx={{ mb: 1 }} onClose={() => setIntakeJsonError(null)}>
                {intakeJsonError}
              </Alert>
            )}
            {intakeJsonSuccess && (
              <Alert severity="success" sx={{ mb: 1 }} onClose={() => setIntakeJsonSuccess(false)}>
                Intake forms saved.
              </Alert>
            )}
            {!intakeJsonLoading && (
              <Box sx={{ mb: 2 }}>
                <Fa12SocialHistoryFields
                  fields={socialFields}
                  setF={setSocialF}
                  packet={{
                    student_first_name: student?.first_name,
                    student_last_name: student?.last_name,
                    student_dob: student?.date_of_birth,
                  }}
                  readOnly={!canEdit}
                />
                <Box sx={{ mt: 3 }}>
                  <Fa17FeedingQuestionnaireFields
                    fields={feedingFields}
                    setF={setFeedingF}
                  />
                </Box>
                {canEdit && (
                  <Button variant="contained" sx={{ mt: 2 }} onClick={handleSaveIntakeForms} disabled={intakeJsonSaving}>
                    {intakeJsonSaving ? 'Saving…' : 'Save social history & feeding'}
                  </Button>
                )}
              </Box>
            )}
          </Paper>
        )}

        {canEdit && (
          <PaperPacketIntakePanel
            open={paperIntakeOpen}
            onClose={() => setPaperIntakeOpen(false)}
            studentId={student?.id}
            documents={documents}
            initialDocumentId={paperIntakeDocId}
            onSaved={() => {
              refreshDocuments();
            }}
          />
        )}

        {canEdit && (
          <MarkCompletePaperDialog
            open={paperMarkOpen}
            onClose={() => setPaperMarkOpen(false)}
            studentId={student?.id}
            documents={documents}
            initialDocumentId={paperMarkDocId}
            canUnmark={canAdminPacket}
            onDone={() => {
              if (student?.id) {
                getParentPacketStatus(student.id)
                  .then((res) => setPacketStatus(res.data))
                  .catch(() => {});
              }
            }}
          />
        )}

        {tab === 2 && (
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" color="primary" gutterBottom>Parent / Guardian</Typography>
            {parentGuardianError && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setParentGuardianError(null)}>{parentGuardianError}</Alert>}
            {parentGuardianSuccess && <Alert severity="success" sx={{ mb: 2 }}>Saved.</Alert>}
            {parentGuardianLoading && <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>}
            {!parentGuardianLoading && parentGuardian && pgFormData && (
              <>
                <Grid container spacing={2}>
                  <Grid item xs={12}><Typography variant="subtitle2" color="text.secondary">Mother</Typography></Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth label="Mother name" value={pgFormData.mother_name || ''} onChange={(e) => setPgFormData((p) => ({ ...p, mother_name: e.target.value }))} disabled={!canEdit} />
                    {prefilledSet.has('mother_name') && <Typography variant="caption" color="text.secondary">Pre-filled from student record.</Typography>}
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField size="small" fullWidth label="Mother cell phone" value={pgFormData.mother_cell_phone || ''} onChange={(e) => setPgFormData((p) => ({ ...p, mother_cell_phone: e.target.value }))} disabled={!canEdit} />
                    {prefilledSet.has('mother_cell_phone') && <Typography variant="caption" color="text.secondary">Pre-filled from student record.</Typography>}
                  </Grid>
                  <Grid item xs={12}><TextField size="small" fullWidth label="Mother work address" value={pgFormData.mother_work_address || ''} onChange={(e) => setPgFormData((p) => ({ ...p, mother_work_address: e.target.value }))} disabled={!canEdit} multiline /></Grid>
                  <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Mother work phone" value={pgFormData.mother_work_phone || ''} onChange={(e) => setPgFormData((p) => ({ ...p, mother_work_phone: e.target.value }))} disabled={!canEdit} /></Grid>

                  <Grid item xs={12} sx={{ mt: 2 }}><Typography variant="subtitle2" color="text.secondary">Father</Typography></Grid>
                  <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Father name" value={pgFormData.father_name || ''} onChange={(e) => setPgFormData((p) => ({ ...p, father_name: e.target.value }))} disabled={!canEdit} /></Grid>
                  <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Father cell phone" value={pgFormData.father_cell_phone || ''} onChange={(e) => setPgFormData((p) => ({ ...p, father_cell_phone: e.target.value }))} disabled={!canEdit} />
                    {prefilledSet.has('father_cell_phone') && <Typography variant="caption" color="text.secondary">Pre-filled from student record.</Typography>}
                  </Grid>
                  <Grid item xs={12}><TextField size="small" fullWidth label="Father work address" value={pgFormData.father_work_address || ''} onChange={(e) => setPgFormData((p) => ({ ...p, father_work_address: e.target.value }))} disabled={!canEdit} multiline /></Grid>
                  <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Father work phone" value={pgFormData.father_work_phone || ''} onChange={(e) => setPgFormData((p) => ({ ...p, father_work_phone: e.target.value }))} disabled={!canEdit} /></Grid>

                  <Grid item xs={12} sx={{ mt: 2 }}><Typography variant="subtitle2" color="text.secondary">Home & Contact</Typography></Grid>
                  <Grid item xs={12}><TextField size="small" fullWidth label="Home address" value={pgFormData.home_address || ''} onChange={(e) => setPgFormData((p) => ({ ...p, home_address: e.target.value }))} disabled={!canEdit} multiline />
                    {prefilledSet.has('home_address') && <Typography variant="caption" color="text.secondary">Pre-filled from student record.</Typography>}
                  </Grid>
                  <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Preferred phone" value={pgFormData.preferred_phone || ''} onChange={(e) => setPgFormData((p) => ({ ...p, preferred_phone: e.target.value }))} disabled={!canEdit} />
                    {prefilledSet.has('preferred_phone') && <Typography variant="caption" color="text.secondary">Pre-filled from student record.</Typography>}
                  </Grid>
                  <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Email" value={pgFormData.email || ''} onChange={(e) => setPgFormData((p) => ({ ...p, email: e.target.value }))} disabled={!canEdit} type="email" />
                    {prefilledSet.has('email') && <Typography variant="caption" color="text.secondary">Pre-filled from student record.</Typography>}
                  </Grid>
                  <Grid item xs={12}><Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><Checkbox checked={!!pgFormData.email_viewed_daily} onChange={(e) => setPgFormData((p) => ({ ...p, email_viewed_daily: e.target.checked }))} disabled={!canEdit} /><Typography variant="body2">Email viewed daily</Typography></Box></Grid>

                  <Grid item xs={12} sx={{ mt: 2 }}><Typography variant="subtitle2" color="text.secondary">Emergency Contacts (up to 3)</Typography></Grid>
                  {(parentGuardian.emergency_contacts || []).map((ec) => (
                    <Grid item xs={12} key={ec.id}>
                      <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1, display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2"><strong>{ec.name}</strong>{ec.relationship ? ` (${ec.relationship})` : ''} — {ec.phone || '—'} {ec.address ? ` · ${ec.address}` : ''}</Typography>
                        {prefilledSet.has('emergency_contact') && ec.order === 1 && <Typography variant="caption" color="text.secondary">Pre-filled from student record.</Typography>}
                        {canEdit && (
                          <Button size="small" color="error" onClick={() => { if (window.confirm('Remove this emergency contact?')) studentAPI.deleteEmergencyContact(id, ec.id).then(() => studentAPI.getParentGuardian(id).then((r) => setParentGuardian(r.data))); }}>Remove</Button>
                        )}
                      </Box>
                    </Grid>
                  ))}
                  {canEdit && (parentGuardian.emergency_contacts || []).length < 3 && (
                    <Grid item xs={12}>
                      <AddEmergencyContactButton studentId={id} onAdded={() => studentAPI.getParentGuardian(id).then((r) => setParentGuardian(r.data))} />
                    </Grid>
                  )}
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center', mt: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PrintIcon />}
                        onClick={() => formsAPI.printCurrentCopy(id, 'emergency').catch((e) => alert(e.response?.data?.detail || e.message || 'Failed to render current copy'))}
                      >
                        Print emergency form — current copy (includes staff edits)
                      </Button>
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PictureAsPdf />}
                        disabled={!signedCopySource || signedCopySource === 'archive'}
                        title={
                          hasDocusealSignedCopy
                            ? undefined
                            : signedCopySource === 'archive'
                              ? 'Packet archive exists but is a pre-signature snapshot — not DocuSeal-signed'
                              : 'No DocuSeal-signed packet PDF on file yet'
                        }
                        onClick={() => {
                          if (!hasDocusealSignedCopy) return;
                          studentAPI.downloadParentPacketArchive(id).catch(() => alert('No signed packet PDF on file for this student.'));
                        }}
                      >
                        {hasDocusealSignedCopy
                          ? 'Print signed copy (as submitted by parent)'
                          : 'Print signed copy (unavailable)'}
                      </Button>
                      {signedCopySource === 'archive' && !hasDocusealSignedCopy && (
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<GetApp />}
                          onClick={() => studentAPI.downloadParentPacketArchive(id).catch(() => alert('No packet archive on file.'))}
                        >
                          Download packet archive (unsigned snapshot)
                        </Button>
                      )}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                      {hasDocusealSignedCopy
                        ? 'The signed copy is the immutable DocuSeal parent-signed packet.'
                        : 'Signed copy is available only after DocuSeal signing completes. The packet archive (if any) is a pre-signature snapshot.'}
                      {' '}The current copy is rendered from today&apos;s data (including staff edits) and is watermarked &quot;CURRENT VERSION&quot;.
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sx={{ mt: 2 }}><Typography variant="subtitle2" color="text.secondary">Medical Providers</Typography></Grid>
                  <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Pediatrician name" value={pgFormData.pediatrician_name || ''} onChange={(e) => setPgFormData((p) => ({ ...p, pediatrician_name: e.target.value }))} disabled={!canEdit} /></Grid>
                  <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Pediatrician phone" value={pgFormData.pediatrician_phone || ''} onChange={(e) => setPgFormData((p) => ({ ...p, pediatrician_phone: e.target.value }))} disabled={!canEdit} /></Grid>
                  <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Dentist name" value={pgFormData.dentist_name || ''} onChange={(e) => setPgFormData((p) => ({ ...p, dentist_name: e.target.value }))} disabled={!canEdit} /></Grid>
                  <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Dentist phone" value={pgFormData.dentist_phone || ''} onChange={(e) => setPgFormData((p) => ({ ...p, dentist_phone: e.target.value }))} disabled={!canEdit} /></Grid>
                  <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Specialist name" value={pgFormData.specialist_name || ''} onChange={(e) => setPgFormData((p) => ({ ...p, specialist_name: e.target.value }))} disabled={!canEdit} /></Grid>
                  <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Specialist specialty" value={pgFormData.specialist_specialty || ''} onChange={(e) => setPgFormData((p) => ({ ...p, specialist_specialty: e.target.value }))} disabled={!canEdit} /></Grid>
                  <Grid item xs={12} sm={4}><TextField size="small" fullWidth label="Specialist phone" value={pgFormData.specialist_phone || ''} onChange={(e) => setPgFormData((p) => ({ ...p, specialist_phone: e.target.value }))} disabled={!canEdit} /></Grid>
                  <Grid item xs={12}><TextField size="small" fullWidth label="Preferred hospital" value={pgFormData.preferred_hospital || ''} onChange={(e) => setPgFormData((p) => ({ ...p, preferred_hospital: e.target.value }))} disabled={!canEdit} /></Grid>

                  <Grid item xs={12} sx={{ mt: 2 }}><Typography variant="subtitle2" color="text.secondary">Insurance</Typography></Grid>
                  <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Child insurance ID" value={pgFormData.child_insurance_id || ''} onChange={(e) => setPgFormData((p) => ({ ...p, child_insurance_id: e.target.value }))} disabled={!canEdit} /></Grid>
                  <Grid item xs={12} sm={6}><TextField size="small" fullWidth label="Insurance type" value={pgFormData.insurance_type || ''} onChange={(e) => setPgFormData((p) => ({ ...p, insurance_type: e.target.value }))} disabled={!canEdit} /></Grid>
                </Grid>
                {canEdit && (
                  <Button variant="contained" sx={{ mt: 2 }} onClick={handleSaveParentGuardian} disabled={parentGuardianSaving}>
                    {parentGuardianSaving ? 'Saving…' : 'Save'}
                  </Button>
                )}
              </>
            )}
            {!parentGuardianLoading && parentGuardian && !pgFormData && <Typography color="text.secondary">Loading form…</Typography>}
            {!parentGuardianLoading && !parentGuardian && !parentGuardianError && <Typography color="text.secondary">No parent/guardian record.</Typography>}
          </Paper>
        )}

        {tab === 3 && (
          <Paper sx={{ p: 3, mt: 3 }}>
            <Typography variant="h6" color="primary" gutterBottom>Parent Packet — 2026-2027 Forms</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Generate and preview individual or combined PDFs for the parent packet. All dates are pulled from the session.
            </Typography>
            {canEdit && (
              <Box sx={{ mb: 2, display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Button
                  variant="contained"
                  startIcon={<Send />}
                  onClick={handleSendPacket}
                  disabled={packetSendLoading || !packetSendEligible}
                  title={
                    packetSendEligible
                      ? undefined
                      : 'Parent packets are sent to center-based students only'
                  }
                >
                  {packetSendLoading ? 'Sending…' : 'Send 2026-2027 Parent Packet'}
                </Button>
                {packetStatus?.packet && (
                  <>
                    <Button variant="outlined" size="small" onClick={handleRemindPacket} disabled={packetRemindLoading}>
                      {packetRemindLoading ? 'Sending…' : 'Send Reminder'}
                    </Button>
                    <Button
                      variant="outlined"
                      size="small"
                      onClick={handleSendPacket}
                      disabled={packetSendLoading || !packetSendEligible}
                    >
                      Resend Packet
                    </Button>
                    {canAdminPacket && (
                      <Button variant="outlined" size="small" color="error" onClick={() => setResetPacketDialogOpen(true)}>
                        Reset Packet
                      </Button>
                    )}
                  </>
                )}
              </Box>
            )}
            {canEdit && !packetSendEligible && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Parent packets are sent to <strong>center-based</strong> students only.
                This student is marked as {(student?.service_type || 'unknown').replace(/_/g, ' ')}
                (related service / SEIT / unknown are excluded).
              </Alert>
            )}
            {packetSendError && (
              <Alert severity="error" onClose={() => setPacketSendError(null)} sx={{ mb: 2 }}>
                {packetSendError}
              </Alert>
            )}
            {packetSendSuccess && (
              <Alert severity="success" onClose={() => setPacketSendSuccess(null)} sx={{ mb: 2 }}>
                {packetSendSuccess}
              </Alert>
            )}
            <Typography variant="subtitle2" sx={{ mt: 2, mb: 1 }}>Packet status</Typography>
            {packetStatusLoading && <Box sx={{ py: 1 }}><CircularProgress size={24} /></Box>}
            {!packetStatusLoading && packetStatus && (
              <Box sx={{ mb: 2 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 1 }}>
                  <Chip
                    icon={packetStatus.overall === 'complete' ? <CheckCircle /> : packetStatus.overall === 'in_progress' ? <Schedule /> : <Cancel />}
                    label={packetStatus.overall === 'complete' ? 'Complete' : packetStatus.overall === 'in_progress' ? 'In Progress' : 'Not Sent'}
                    color={packetStatus.overall === 'complete' ? 'success' : packetStatus.overall === 'in_progress' ? 'warning' : 'default'}
                    size="small"
                  />
                  {packetStatus.paper_completed && (
                    <Chip
                      label="Paper"
                      size="small"
                      color="secondary"
                      variant="outlined"
                      title={
                        packetStatus.paper_completed_by_name
                          ? `Paper submission marked by ${packetStatus.paper_completed_by_name}${packetStatus.paper_completed_at ? ` on ${new Date(packetStatus.paper_completed_at).toLocaleDateString()}` : ''} — no e-signature`
                          : 'Completed via paper submission — no e-signature'
                      }
                    />
                  )}
                  {packetStatus.packet && canEdit && (
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            size="small"
                            checked={!!(packetStatus.reviewed ?? packetStatus.packet?.reviewed)}
                            disabled={packetReviewedLoading}
                            onChange={(e) => {
                              const next = e.target.checked;
                              setPacketReviewedLoading(true);
                              setParentPacketReviewed(student.id, next)
                                .then((res) => {
                                  const payload = res.data || {};
                                  setPacketStatus((prev) => {
                                    if (!prev) return prev;
                                    return {
                                      ...prev,
                                      ...payload,
                                      packet: prev.packet
                                        ? { ...prev.packet, ...payload }
                                        : prev.packet,
                                    };
                                  });
                                })
                                .catch((err) => {
                                  alert(err.response?.data?.detail || err.message || 'Failed to update reviewed');
                                })
                                .finally(() => setPacketReviewedLoading(false));
                            }}
                          />
                        }
                        label="Mark reviewed"
                        sx={{ mr: 0 }}
                      />
                      {(packetStatus.reviewed ?? packetStatus.packet?.reviewed) && (
                        <Typography variant="body2" color="text.secondary">
                          Reviewed by{' '}
                          {packetStatus.reviewed_by_name
                            || packetStatus.packet?.reviewed_by_name
                            || 'staff'}
                          {' '}on{' '}
                          {(() => {
                            const iso = packetStatus.reviewed_at || packetStatus.packet?.reviewed_at;
                            if (!iso) return '—';
                            const d = new Date(iso);
                            return Number.isNaN(d.getTime())
                              ? '—'
                              : d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
                          })()}
                        </Typography>
                      )}
                    </Box>
                  )}
                  {packetStatus.packet?.packet_url && (
                    <Button variant="outlined" size="small" startIcon={<ContentCopy />} onClick={handleCopyPacketLink}>
                      {packetLinkCopied ? 'Copied!' : 'Copy parent link'}
                    </Button>
                  )}
                  {hasDocusealSignedCopy && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<GetApp />}
                      onClick={handleDownloadPacketArchive}
                      disabled={packetArchiveLoading}
                    >
                      {packetArchiveLoading ? 'Downloading…' : 'Download signed copy'}
                    </Button>
                  )}
                  {signedCopySource === 'archive' && !hasDocusealSignedCopy && (
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<GetApp />}
                      onClick={handleDownloadPacketArchive}
                      disabled={packetArchiveLoading}
                      title="Pre-signature archive snapshot — not DocuSeal-signed"
                    >
                      {packetArchiveLoading ? 'Downloading…' : 'Download packet archive (unsigned)'}
                    </Button>
                  )}
                </Box>
                {packetStatus.forms && packetStatus.forms.length > 0 && (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Form</TableCell>
                          <TableCell>Status</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {packetStatus.forms.map((f) => (
                          <TableRow key={f.form_id}>
                            <TableCell>{f.form_name}</TableCell>
                            <TableCell>
                              {f.status === 'signed' || f.is_complete ? (
                                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                  <Chip
                                    icon={<CheckCircle />}
                                    label={f.completion_source === 'paper' ? 'Complete (paper)' : 'Signed'}
                                    size="small"
                                    color="success"
                                  />
                                  {f.completion_source === 'paper' && (
                                    <Chip label="Paper" size="small" color="secondary" variant="outlined" />
                                  )}
                                </Box>
                              ) : (
                                <Chip icon={<Schedule />} label="Not started" size="small" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
            )}
            <Button variant="contained" startIcon={<Visibility />} onClick={handlePacketPreviewAll} sx={{ mb: 2 }}>
              Preview All (combined PDF)
            </Button>
            {packetFormsLoading && <Box sx={{ py: 2 }}><CircularProgress size={24} /></Box>}
            {!packetFormsLoading && packetForms.length > 0 && (
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Form</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {packetForms.map((f) => (
                      <TableRow key={f.form_id}>
                        <TableCell>{f.name}</TableCell>
                        <TableCell><Chip label={f.type} size="small" variant="outlined" /></TableCell>
                        <TableCell>{f.signed ? <Chip label="Signed" size="small" color="success" /> : <Chip label="Not signed" size="small" />}</TableCell>
                        <TableCell>
                          <Button size="small" startIcon={<PictureAsPdf />} onClick={() => handlePacketFormPdf(f.form_id)}>
                            Preview PDF
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
        )}

        {tab === 5 && (
          <Box sx={{ mt: 3 }}>
            <IepServicesTab studentId={Number(id)} />
          </Box>
        )}

        <Dialog open={resetPacketDialogOpen} onClose={() => !resetPacketLoading && setResetPacketDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Reset parent packet</DialogTitle>
          <DialogContent>
            <Typography variant="body2">
              Are you sure you want to reset this packet? The parent will need to start over from the beginning.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setResetPacketDialogOpen(false)} disabled={resetPacketLoading}>Cancel</Button>
            <Button color="error" variant="contained" onClick={handleConfirmResetPacket} disabled={resetPacketLoading}>
              {resetPacketLoading ? 'Resetting…' : 'Reset'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={uploadModalOpen} onClose={() => !docUploading && setUploadModalOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>Upload document</DialogTitle>
          <DialogContent>
            <input
              ref={docUploadInputRef}
              type="file"
              style={{ display: 'none' }}
              onChange={(e) => setDocUploadFile(e.target.files?.[0] || null)}
            />
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <Button variant="outlined" onClick={() => docUploadInputRef.current?.click()}>
                {docUploadFile ? docUploadFile.name : 'Choose file'}
              </Button>
              <FormControl size="small" fullWidth>
                <InputLabel>Category</InputLabel>
                <Select
                  value={docUploadCategory}
                  label="Category"
                  onChange={(e) => setDocUploadCategory(e.target.value)}
                >
                  {DOC_CATEGORIES.map((c) => (
                    <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>
                  ))}
                </Select>
              </FormControl>
              {docUploadCategory === 'other_custom' && (
                <TextField
                  size="small"
                  label="Custom category name"
                  value={docUploadCustomCategory}
                  onChange={(e) => setDocUploadCustomCategory(e.target.value)}
                  required
                  fullWidth
                />
              )}
              <TextField size="small" label="Document title (e.g. Speech Evaluation March 2026)" value={docUploadTitle} onChange={(e) => setDocUploadTitle(e.target.value)} fullWidth />
              <TextField size="small" label="Description (optional)" value={docUploadDescription} onChange={(e) => setDocUploadDescription(e.target.value)} multiline rows={2} fullWidth />
              <TextField size="small" type="date" label="Date of document" value={docUploadDateOfDocument} onChange={(e) => setDocUploadDateOfDocument(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => !docUploading && setUploadModalOpen(false)}>Cancel</Button>
            <Button variant="contained" onClick={handleDocUpload} disabled={!docUploadFile || docUploading || (docUploadCategory === 'other_custom' && !(docUploadCustomCategory || '').trim())}>
              {docUploading ? 'Uploading…' : 'Upload'}
            </Button>
          </DialogActions>
        </Dialog>

        <Dialog open={Array.isArray(exitPackageMissing) && exitPackageMissing.length > 0} onClose={() => setExitPackageMissing(null)} maxWidth="sm" fullWidth>
          <DialogTitle>Missing documents for Exit Package</DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ mb: 2 }}>The following categories need at least one document before the Exit Package can be generated:</Typography>
            <ul style={{ margin: 0, paddingLeft: 20 }}>
              {exitPackageMissing?.map((label, i) => (
                <li key={i}><Typography variant="body2">{label}</Typography></li>
              ))}
            </ul>
            <Typography variant="body2" sx={{ mt: 2 }}>Notify a staff member to upload the missing documents:</Typography>
            <FormControl size="small" fullWidth sx={{ mt: 1 }}>
              <InputLabel>Staff member</InputLabel>
              <Select value={exitNotifyUserId} label="Staff member" onChange={(e) => setExitNotifyUserId(e.target.value)}>
                <MenuItem value="">Select…</MenuItem>
                {staffUsers.map((u) => (
                  <MenuItem key={u.id} value={u.id}>{u.email}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setExitPackageMissing(null)}>Close</Button>
            <Button variant="contained" disabled={!exitNotifyUserId || exitNotifySending} onClick={() => handleExitNotify('email')}>
              {exitNotifySending ? 'Sending…' : 'Send Email'}
            </Button>
            <Button variant="outlined" disabled={!exitNotifyUserId || exitNotifySending} onClick={() => handleExitNotify('sms')}>
              Send Text (SMS)
            </Button>
          </DialogActions>
        </Dialog>

        {user?.role === 'admin' && (student.ssn != null || student.medical_info != null) && (
          <Paper sx={{ p: 3, mt: 2 }}>
            <Typography variant="h6" color="primary" gutterBottom>Sensitive (admin only)</Typography>
            <Field label="SSN" value={student.ssn ? '*** on file ***' : null} />
            <Field label="Medical info" value={student.medical_info ? '*** on file ***' : null} />
          </Paper>
        )}
      </Container>
    </Box>
  );
};

export default StudentDetailView;
